import { FC, ReactNode } from 'react';

import { usePage } from '@inertiajs/react';
import { Filter } from 'lucide-react';

import { ConfiguredPropertyFilter } from './ConfiguredPropertyFilter';
import { expansionFilterOptions } from './expansionFilterOptions';
import { PropertyFilterSelect } from './PropertyFilterSelect';
import { Button } from '../../../components/ui/Button';
import { Expansion, ProductFilter, ProductType } from '../../../types';
import { PropertyOption } from '../../../types/shared';
import { cn } from '../../../utils';

type PageProps = {
    currentProductType: ProductType | null;
    filtersConfig: Record<string, ProductFilter[]>;
};

interface Props {
    readonly filters: Record<string, string>;
    readonly onFilterChange: (filters: {
        q: Record<string, string>;
        expansionSlug?: string;
        preserveState?: boolean;
    }) => void;
    readonly onResetFilters: () => void;
    readonly expansions: Expansion[] | null;
    readonly expansion: Expansion | null;
    readonly extraFilters?: {
        key: string;
        label: string;
        allLabel: string;
        options: PropertyOption[];
        searchable?: boolean;
        multiselect?: boolean;
        testId?: string;
    }[];
    readonly fallbackProductTypeSlug?: string;
    readonly hidePropertyFiltersWithoutProductType?: boolean;
    readonly sticky?: boolean;
    readonly topContent?: ReactNode;
    readonly variant?: 'default' | 'seller';
    readonly className?: string;
}

interface FilterFieldProps {
    readonly label: string;
    readonly children: ReactNode;
}

const FilterField: FC<FilterFieldProps> = ({ label, children }) => (
    <div>
        <label className="mb-1.5 block text-sm font-medium">{label}</label>
        {children}
    </div>
);

export const DesktopFilters: FC<Props> = ({
    filters,
    onFilterChange,
    onResetFilters,
    expansion,
    expansions,
    extraFilters,
    fallbackProductTypeSlug,
    hidePropertyFiltersWithoutProductType = false,
    sticky = true,
    topContent,
    variant = 'default',
    className,
}) => {
    const { currentProductType, filtersConfig } = usePage<PageProps>().props;
    const expansionOptions = expansionFilterOptions(expansions);
    const resolvedFallbackProductTypeSlug =
        (fallbackProductTypeSlug && filtersConfig[fallbackProductTypeSlug]
            ? fallbackProductTypeSlug
            : undefined) ?? Object.keys(filtersConfig)[0];
    const currentFiltersConfig =
        (hidePropertyFiltersWithoutProductType && !currentProductType
            ? []
            : currentProductType
              ? filtersConfig[currentProductType.slug]
              : filtersConfig[resolvedFallbackProductTypeSlug]) ?? [];
    const isSellerVariant = variant === 'seller';
    const filterTriggerClassName = cn(
        'rounded-xl',
        isSellerVariant &&
            'border-border/70 bg-muted/20 shadow-none transition-colors hover:bg-muted/35',
    );
    const filterContentClassName = cn(
        'rounded-2xl',
        isSellerVariant && 'border-border/70 shadow-xl',
    );

    const handlePropertyChange = (
        updates: Record<string, string>,
        preserveState = false,
    ) => {
        onFilterChange({
            q: {
                ...filters,
                ...updates,
            },
            expansionSlug: expansion?.slug,
            preserveState,
        });
    };

    const handleChange = (
        property: string,
        value: string,
        preserveState = false,
    ) => {
        handlePropertyChange({ [property]: value }, preserveState);
    };

    return (
        <aside
            className={cn(
                'hidden w-full min-w-0 lg:block',
                sticky && 'lg:sticky lg:top-28 lg:z-30',
                className,
            )}
            data-testid="desktop-filters"
        >
            <div
                className={cn(
                    sticky &&
                        'lg:max-h-[calc(100vh-8rem)] lg:overflow-x-hidden lg:overflow-y-auto lg:pr-1',
                )}
                data-testid="desktop-filters-scroll"
            >
                <div
                    className={cn(
                        'rounded-2xl border border-border/70 bg-background p-4 shadow-sm',
                        isSellerVariant && 'bg-muted/10',
                    )}
                    data-testid="desktop-filters-surface"
                >
                    <div
                        className="flex flex-col gap-5"
                        data-testid="desktop-filters-row"
                    >
                        {topContent && (
                            <div className="border-b border-border/70 pb-4">
                                {topContent}
                            </div>
                        )}

                        <div className="flex items-center font-medium">
                            <Filter className="mr-2 h-4 w-4" />
                            <span data-testid="desktop-filters-label">
                                Фільтри
                            </span>
                        </div>

                        <div className="space-y-4">
                            {extraFilters?.map((filter) => (
                                <FilterField
                                    key={filter.key}
                                    label={filter.label}
                                >
                                    <PropertyFilterSelect
                                        value={filters[filter.key] || 'all'}
                                        onChange={(value) =>
                                            handleChange(filter.key, value)
                                        }
                                        options={filter.options}
                                        searchable={filter.searchable}
                                        multiselect={filter.multiselect}
                                        allLabel={filter.allLabel}
                                        size="compact"
                                        testId={filter.testId}
                                        triggerClassName={
                                            filterTriggerClassName
                                        }
                                        contentClassName={
                                            filterContentClassName
                                        }
                                    />
                                </FilterField>
                            ))}

                            {expansionOptions.length > 0 &&
                                currentProductType?.slug !== 'accessories' &&
                                currentProductType?.slug !== 'sealed' && (
                                    <FilterField label="Колекції">
                                        <PropertyFilterSelect
                                            value={expansion?.slug || 'all'}
                                            onChange={(value) =>
                                                onFilterChange({
                                                    q: filters,
                                                    expansionSlug: value,
                                                })
                                            }
                                            options={expansionOptions}
                                            searchable
                                            showSelectionIndicator={false}
                                            allLabel="Усі колекції"
                                            searchPlaceholder="Пошук колекцій..."
                                            emptyText="Колекції не знайдено"
                                            size="compact"
                                            testId="expansion-filter"
                                            triggerClassName={
                                                filterTriggerClassName
                                            }
                                            contentClassName={
                                                filterContentClassName
                                            }
                                        />
                                    </FilterField>
                                )}

                            {currentFiltersConfig.map((productProperty) => (
                                <FilterField
                                    key={productProperty.id}
                                    label={productProperty.name}
                                >
                                    <ConfiguredPropertyFilter
                                        property={productProperty}
                                        values={filters}
                                        onChange={handlePropertyChange}
                                        size="compact"
                                        triggerClassName={
                                            filterTriggerClassName
                                        }
                                        contentClassName={
                                            filterContentClassName
                                        }
                                    />
                                </FilterField>
                            ))}
                        </div>

                        <Button
                            variant="outline"
                            className="h-9 w-full rounded-xl px-3 text-sm"
                            data-testid="desktop-filters-reset"
                            onClick={onResetFilters}
                        >
                            Очистити фільтри
                        </Button>
                    </div>
                </div>
            </div>
        </aside>
    );
};
